import { hash } from '@ember/helper';
import { guidFor } from '@ember/object/internals';
import Component from '@glimmer/component';
import type { WithBoundArgs } from '@glint/template';
import Tick from '../../icons/tick.gts';
import CheckboxControl from '../controls/checkbox.gts';
import RadioControl from '../controls/radio.gts';

export interface ChoiceFieldSig {
  Args: {
    name?: string;
    label?: string;
    variant?: 'toggle-button';
    isChecked?: boolean;
  };
  Blocks: {
    default: [
      {
        Checkbox: WithBoundArgs<typeof CheckboxControl, 'id' | 'styles'>;
        Radio: WithBoundArgs<typeof RadioControl, 'id' | 'styles'>;
      }
    ];
  };
}

/** Factorize the toggle button styles for a HTMLLabelElement wrapping a radio or a checkbox */
export default class ChoiceField extends Component<ChoiceFieldSig> {
  get uniqueId() {
    return guidFor(this);
  }

  get labelStyles() {
    const styles = ['flex items-center gap-2 justify-start font-sans'];

    if (this.args.variant === 'toggle-button') {
      styles.push(
        'border',
        'border-grey-medium',
        'border-l-0',
        'first:border-l',
        'cursor-pointer',
        'py-2',
        'px-4',
        'first:rounded-l-md',
        'last:rounded-r-md',
        'select-none',
        'has-[input:focus-visible]:ring-inset',
        'has-[input:focus-visible]:ring-purple',
        'has-[input:focus-visible]:ring-2'
      );

      if (this.args.isChecked) {
        styles.push('bg-purple-light', 'text-purple');
      } else {
        styles.push('bg-white', 'text-grey');
      }
    }

    return styles.join(' ');
  }

  get showTickIcon() {
    return (
      this.args.variant === 'toggle-button' && this.args.isChecked === true
    );
  }

  get controlStyles() {
    return this.args.variant === 'toggle-button' ? 'sr-only' : '';
  }

  <template>
    <label class={{this.labelStyles}} for={{guidFor this}}>
      {{#if this.showTickIcon}}
        <Tick @class='text-purple' />
      {{/if}}

      {{yield
        (hash
          Checkbox=(component
            CheckboxControl id=this.uniqueId styles=this.controlStyles
          )
          Radio=(component
            RadioControl id=this.uniqueId styles=this.controlStyles
          )
        )
      }}

      {{@label}}
    </label>
  </template>
}
